import {
	AfterContentChecked,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	inject,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import {
	ItIconComponent,
	ItModalComponent,
	ItButtonDirective,
	ItProgressButtonComponent,
	ItInputComponent,
	ItSelectComponent,
	ItCheckboxComponent,
	ItFileUtils,
} from 'design-angular-kit';
import { switchMap } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { IterlexService } from '../iterlex.service';
import { DocumentComponent } from '../document/document.component';
import type { Authority } from '../interfaces/authority';
import type { Dossier } from '../interfaces/dossier';
import type { Phase } from '../interfaces/phase';
import type { Step } from '../interfaces/step';
import type { Document } from '../interfaces/document';
import type { DocumentType } from '../interfaces/document-type';
import type { DocumentSubtype } from '../interfaces/document-subtype';
import type { File } from '../interfaces/file';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

@Component({
	selector: 'app-step',
	imports: [
		FormsModule,
		ItIconComponent,
		ItButtonDirective,
		ItProgressButtonComponent,
		ItModalComponent,
		ItInputComponent,
		ItSelectComponent,
		ItCheckboxComponent,
		DocumentComponent,
		TranslateModule,
	],
	templateUrl: './step.component.html',
	styleUrl: './step.component.scss',
})
export class StepComponent implements OnInit, AfterContentChecked {
	private router = inject(Router);
	private iterlexService = inject(IterlexService);

	wasLoaded = false;
	documents: Document[] = [];
	files: File[] = [];
	selectedFiles = new Set<string>();
	authorities: Authority[] = [];
	currentDocument?: Document;
	progressSendToStagingArea = false;
	progressDeleteDocumentUri = '';
	@Input({ required: true }) dossier!: Dossier;
	@Input({ required: true }) phase!: Phase;
	@Input({ required: true }) authority!: Authority;
	@Input({ required: true }) body!: Authority;
	@Input({ required: true }) step!: Step;
	@Input({ required: true }) index!: number;
	@Input({ required: true }) active!: boolean;
	@Output() setCurrentPhaseEvent = new EventEmitter<string>();
	@Output() changeCurrentStepIdEvent = new EventEmitter<string>();
	@Output() deleteStepEvent = new EventEmitter<Step>();
	@Output() sendedToStagingAreaEvent = new EventEmitter();
	documentTypes: DocumentType[] = [];
	documentSubtypes: DocumentSubtype[] = [];
	currentFile!: globalThis.File;
	languages = environment.documentLanguages;
	isSharePhase = false;
	ngOnInit(): void {
		this.isSharePhase =
			this.phase.id.includes('condivisione') &&
			this.step.id === 'inUscita';
		if (this.isSharePhase) {
			this.iterlexService.getAuthorities().subscribe((authorities) => {
				this.authorities = authorities;
			});
		}
	}
	ngAfterContentChecked(): void {
		if (this.active && !this.wasLoaded) {
			this.wasLoaded = true;
			this.getDocuments();
			this.iterlexService
				.getDocumentTypes(
					new HttpParams().append('authority', this.authority.id),
				)
				.subscribe((documentTypes) => {
					this.documentTypes = documentTypes;
				});
			this.iterlexService
				.getDocumentSubtypes(
					new HttpParams().append('authority', this.authority.id),
				)
				.subscribe((documentSubtypes) => {
					this.documentSubtypes = documentSubtypes;
				});
		}
		if (!this.active && this.wasLoaded) {
			this.wasLoaded = false;
		}
	}
	getDocuments() {
		this.iterlexService
			.getDocuments(
				new HttpParams()
					.append('dossierUri', this.dossier.workUri)
					.append('authority', this.authority.id)
					.append('body', this.body.id)
					.append('phaseName', this.phase.id)
					.append('stepId', this.step.id),
			)
			.subscribe((documents) => {
				this.documents = documents.value;
			});
	}
	async getFiles() {
		this.files = [];
		for (const document of this.documents) {
			const documentComplete = await firstValueFrom(
				this.iterlexService.getDocumentComplete(
					this.authority.id,
					this.body.id,
					document.workUri,
					this.phase.id,
					this.step.id,
					this.dossier.workUri,
				),
			);
			for (const version of documentComplete.versions) {
				for (const file of version.files) {
					this.files.push(file);
					this.selectedFiles.add(file.manifestationUri);
				}
			}
		}
	}
	onEditStepSubmit(form: NgForm) {
		if (form.valid) {
			this.iterlexService
				.editStep(
					this.dossier.workUri,
					this.authority.id,
					this.body.id,
					this.phase.id,
					this.step.id,
					form.value.descriptionITA,
					form.value.descriptionDEU,
				)
				.subscribe(() => {
					this.step.id = this.iterlexService.toCamelCase(
						form.value.descriptionITA,
					);
					this.changeCurrentStepIdEvent.emit(this.step.id);
					this.router.navigate([], {
						queryParams: { step: this.step.id },
						queryParamsHandling: 'merge',
					});
					this.step.label['it'] = form.value.descriptionITA;
					this.step.label['de'] = form.value.descriptionDEU;
				});
		}
	}
	deleteStep() {
		this.iterlexService
			.deleteStep(
				this.authority.id,
				this.body.id,
				this.dossier.workUri,
				this.phase.id,
				this.step.id,
			)
			.subscribe(() => {
				this.deleteStepEvent.emit(this.step);
			});
	}
	setCurrentFile(files: FileList | null) {
		if (files) {
			this.currentFile = files[0];
		}
	}
	onCreateDocumentSubmit(form: NgForm) {
		if (form.valid) {
			const [documentType, documentSubtype] =
				form.value.documentSubtype.split('/');
			this.iterlexService
				.createDocument(
					this.authority.id,
					this.body.id,
					documentType,
					documentSubtype,
					this.dossier.author,
					form.value.date,
					form.value.number,
					form.value.title,
					this.dossier.workUri,
					'main',
				)
				.subscribe((document) => {
					this.iterlexService
						.createVersion(
							document.workUri,
							document.workUri,
							form.value.number,
							this.authority.id,
							this.body.id,
							form.value.language,
							document.title,
							this.iterlexService.dateTimeWithTimeZone(
								form.value.date,
								form.value.time,
							),
							document.author,
						)
						.subscribe((version) => {
							this.iterlexService
								.setVersionStep(
									this.authority.id,
									this.body.id,
									version.expressionUri,
									this.phase.id,
									this.step.id,
								)
								.subscribe(() => {
									if (form.value.file) {
										ItFileUtils.fileToBase64(
											this.currentFile,
										)
											.pipe(
												switchMap((base64) => {
													return this.iterlexService.createFile(
														version.expressionUri,
														this.authority.id,
														this.body.id,
														this.currentFile.name,
														version.date,
														this.body.id,
														base64,
													);
												}),
											)
											.subscribe(() =>
												this.getDocuments(),
											);
									} else {
										this.getDocuments();
									}
								});
						});
				});
		}
	}
	deleteDocument(workUri: string) {
		this.progressDeleteDocumentUri = workUri;
		this.iterlexService
			.deleteDocument(workUri, this.authority.id, this.body.id)
			.subscribe({
				next: () => {
					this.getDocuments();
				},
				complete: () => {
					this.progressDeleteDocumentUri = '';
				},
			});
	}
	async onSendToStagingAreaSubmit(form: NgForm) {
		if (form.valid) {
			this.progressSendToStagingArea = true;
			const expressionUris: string[] = [];
			for (const document of this.documents) {
				const documentComplete = await firstValueFrom(
					this.iterlexService.getDocumentComplete(
						this.authority!.id,
						this.body!.id,
						document.workUri,
						this.phase.id,
						this.step.id,
					),
				);
				for (const version of documentComplete.versions) {
					expressionUris.push(version.expressionUri);
				}
			}
			this.iterlexService
				.sendToStagingArea(
					this.authority!.id,
					this.body!.id,
					this.dossier!.workUri,
					this.phase.id,
					this.step.id,
					expressionUris,
					form.value.authority,
				)
				.subscribe({
					next: () => {
						this.sendedToStagingAreaEvent.emit();
					},
					complete: () => {
						this.progressSendToStagingArea = false;
					},
				});
		}
	}
	orderFiles(index: number, change: number) {
		[this.files[index], this.files[index + change]] = [
			this.files[index + change],
			this.files[index],
		];
	}
	onSendToBedeSubmit(form: NgForm) {
		if (form.valid) {
			const files = [...this.selectedFiles];
			this.iterlexService
				.getSteps(
					this.authority.id,
					this.body.id,
					this.dossier.workUri,
					this.phase.id,
				)
				.subscribe((steps) => {
					this.iterlexService
						.sendToBede(
							this.authority.id,
							this.body.id,
							this.dossier.workUri,
							this.phase.id,
							this.step.id,
							this.phase.id,
							steps.at(-1)!.id,
							files[0],
							files.slice(1),
						)
						.subscribe(() => {
							this.selectedFiles = new Set();
						});
				});
		}
	}
}
